import { appInsights } from "utils/appInsights";
import { calculateDynamicConfig, shouldUseDynamicConfig, getCurrentAppType } from "utils/dynamicConfig";

interface EntraIDConfig {
  clientId: string;
  tenantId: string;
  authServerName: string;
  userImpersonationScope: string;
  redirectUri: string;
  postLogoutRedirectUri: string;
}

interface APIConfig {
  opsApiUrl: string,
  orderApiUrl: string;
  negotiateUrlSignalR: string;
}

interface MonitoringConfig {
  appInsightsKey: string;
}

interface ThirdPartyConfig {
  agGridLicenseKey: string;
}

interface AppConfig {
  entraID: EntraIDConfig;
  api: APIConfig;
  monitoring: MonitoringConfig;
  thirdParty: ThirdPartyConfig;
  version: string;
}

// Function to safely load environment variables
const getEnv = (key: string, defaultValue: string = ""): string => {
  return import.meta.env[key] ?? defaultValue;
};

// Get dynamic configuration based on current URL (only for production environments)
const dynamicConfig = calculateDynamicConfig();
const useDynamicConfig = shouldUseDynamicConfig();

// Type-safe config object
const appConfig: AppConfig = {
  entraID: {
    clientId: getEnv("VITE_ENTRAID_CLIENT_ID"),
    tenantId: getEnv("VITE_ENTRAID_TENANT_ID"),
    authServerName: getEnv("VITE_ENTRAID_AUTH_SERVER_NAME"),
    userImpersonationScope: getEnv("VITE_ENTRAID_USER_IMPERSONATION_SCOPE"),
    redirectUri: useDynamicConfig 
      ? (getCurrentAppType().includes('ops') 
          ? `${window.location.protocol}//${window.location.hostname}` 
          : dynamicConfig.entraIdRedirectUri)
      : getEnv("VITE_ENTRAID_REDIRECT_URI"),
    postLogoutRedirectUri: useDynamicConfig 
      ? dynamicConfig.entraIdRedirectUri
      : getEnv("VITE_ENTRAID_POST_LOGOUT_REDIRECT_URI"),
  },
  api: {
    opsApiUrl: useDynamicConfig ? dynamicConfig.opsApiUrl : getEnv("VITE_OPS_API_URL"),
    orderApiUrl: useDynamicConfig ? dynamicConfig.orderApiUrl : getEnv("VITE_ORDER_API_URL"),
    negotiateUrlSignalR: useDynamicConfig ? dynamicConfig.orderApiUrlNegotiateSignalR : getEnv("VITE_ORDER_API_URL_NEGOTIATE_SIGNALR"),
  },
  monitoring: {
    appInsightsKey: getEnv("VITE_APPINSIGHTS_INSTRUMENTATION_KEY"),
  },
  thirdParty: {
    agGridLicenseKey: getEnv("VITE_AG_GRID_LICENSE_KEY"),
  },
  version: getEnv("VITE_APP_VERSION"),
};

export default appConfig;
